import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  CorporateAccountOpenPages,
  useCorporateAccountOpenPageTypeStore,
} from 'views/pages/accountOpen/corporate/hooks';
import { useEffect } from 'react';
import { useCorporateAccountEstablishStore } from '../hooks';
import { ErrorWord, TemplateProps } from '../common';
import { formErrorScroll } from 'views/mutual/formErrorScroll';
import { PageStep } from 'views/pages/accountInfo/hooks';
import {
  AdvanceQuestionMutationVariables,
  useChangeAccountInfoMutation,
  useAdvanceQuestionMutation,
} from 'views/utils/graphql/graphql';
import { useGlobalToastHandler } from 'views/mutual/globalToast/hooks';
import useOnceSubmit from 'views/utils/useOnceSubmit';

export type AdvanceQuestionType = {
  uniqueId: string | null;
  dealerQuestion: boolean | null;
  identificationQuestion: boolean | null;
  mailAddressQuestion: string | null;
  selfieQuestion: boolean | null;
  personalInformation: boolean;
  generalTermsCondition: boolean;
  confirmResponsibility: boolean;
};

export type DisplayItemProps = {
  label: string;
  value: string;
  hidden?: boolean;
};

export const useCorporateAccountAdvancedQuestion = ({ isUpdate, setPageStep }: TemplateProps) => {
  const { param, add } = useCorporateAccountEstablishStore();
  const { setAccountOpenPageType } = useCorporateAccountOpenPageTypeStore();
  const [_advanceQuestionResponse, advanceQuestion] = useAdvanceQuestionMutation();
  const [_changeAccountResponse, changeAccount] = useChangeAccountInfoMutation();
  const { showError } = useGlobalToastHandler();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const advanceQuestionValidation = Yup.object().shape({
    dealerQuestion: !isUpdate ? Yup.bool().required(ErrorWord.SELECT) : Yup.bool().nullable(),
    mailAddressQuestion: !isUpdate
      ? Yup.string().email(ErrorWord.EMAIL).required(ErrorWord.EMAIL_NONE)
      : Yup.bool().nullable(),
    identificationQuestion: Yup.bool().required(ErrorWord.SELECT),
    selfieQuestion: Yup.bool().required(ErrorWord.SELECT),
  });

  const initialValues: AdvanceQuestionType = {
    uniqueId: param.uniqueId ?? null,
    dealerQuestion: param.dealerQuestion ?? null,
    identificationQuestion: param.identificationQuestion ?? null,
    mailAddressQuestion: param.mailAddressQuestion ?? null,
    selfieQuestion: param.selfieQuestion ?? null,
    personalInformation: param.personalInformation ?? false,
    generalTermsCondition: param.generalTermsCondition ?? false,
    confirmResponsibility: param.confirmResponsibility ?? false,
  };

  const executeOpenAccount = useOnceSubmit<void>(
    {
      onSubmit: async () => {
        const data: AdvanceQuestionMutationVariables = {
          input: {
            CorporateAccount: {
              RepresentAndDealerAreSame: formik.values.dealerQuestion ?? false,
              HaveEKYCDocument: formik.values.identificationQuestion ?? false,
              CanTakePicture: formik.values.selfieQuestion ?? false,
              DealerMailAddress: formik.values.mailAddressQuestion ?? '',
            },
          },
        };
        const openAccountData = await advanceQuestion(data);
        if (!openAccountData?.data) return;

        if (openAccountData.data.AdvanceQuestion?.Code === 0) {
          setAccountOpenPageType(CorporateAccountOpenPages.completeSendMail);
        } else {
          showError(openAccountData.data.AdvanceQuestion?.Message);
        }
      },
    },
    [showError],
  );

  const executeUpdateAccount = () => {
    add({
      ...param,
      ...formik.values,
    });
    setPageStep && setPageStep(PageStep.UPDATE);
  }

  const formik = useFormik<AdvanceQuestionType>({
    initialValues,
    onSubmit: async () => {
      if (isUpdate) {
        await executeUpdateAccount();
      } else {
        await executeOpenAccount();
      }
    },
    validationSchema: advanceQuestionValidation,
    validateOnChange: false,
    validateOnBlur: false,
  });

  const dealerQuestionProps = {
    selected: formik.values.dealerQuestion,
    onChange: (value: boolean) => formik.setFieldValue('dealerQuestion', value),
    error: formik.errors.dealerQuestion,
  };

  const identificationQuestionProps = {
    selected: formik.values.identificationQuestion,
    onChange: (value: boolean) => formik.setFieldValue('identificationQuestion', value),
    error: formik.errors.identificationQuestion,
  };

  const mailAddressQuestionProps = {
    selected: formik.values.mailAddressQuestion,
    onChange: (value: string) => formik.setFieldValue('mailAddressQuestion', value),
    error: formik.errors.mailAddressQuestion,
  };

  const selfieQuestionProps = {
    selected: formik.values.selfieQuestion,
    onChange: (value: boolean) => formik.setFieldValue('selfieQuestion', value),
    error: formik.errors.selfieQuestion,
  };

  const personalInformationProps = {
    checked: formik.values.personalInformation,
    setChecked: (value: boolean) => formik.setFieldValue('personalInformation', value),
  };

  const generalTermsConditionProps = {
    checked: formik.values.generalTermsCondition,
    setChecked: (value: boolean) => formik.setFieldValue('generalTermsCondition', value),
  };
  const confirmResponsibilityProps = {
    checked: formik.values.confirmResponsibility,
    setChecked: (value: boolean) => formik.setFieldValue('confirmResponsibility', value),
  };

  useEffect(() => {
    formErrorScroll();
  }, [formik.isSubmitting]);

  return {
    formik,
    dealerQuestionProps,
    identificationQuestionProps,
    mailAddressQuestionProps,
    selfieQuestionProps,
    personalInformationProps,
    generalTermsConditionProps,
    confirmResponsibilityProps,
  };
};
